import { createSlice } from '@reduxjs/toolkit';

import { getCourseURL } from '../api/commonHTTP';
import { DEFAULT_PAGE_LOAD_SIZE } from '../api/pagination';
import { getEmptyPaginationState } from '../utils/objectUtils';

import { apiCallBegan, apiVersion } from './api';

const slice = createSlice({
    name: 'courseService',
    initialState: {
        courseAuditData: [],
        courseModules: [],
        courseModulesMetaData: {},
        courseModulesByUser: {},
        modulesByParticipantAndDateRange: {},
        modulesByParticipantAndDateRangeMetaData: {},
        newScheduledModules: {},
        courseModulesByUserMetaData: {},
        courseModulesByUsersAndDate: {},
        courseModulesByUsersAndDateMetaData: {},
        courseModulesByUserAndDateRange: {},
        courseModulesByUserAndDateRangeMetaData: {},
        courseProvisions: [],
        courseProvisionsMetaData: {},
        courses: [],
        coursesMetaData: getEmptyPaginationState(),
        coursesByCourseIds: [],
        coursesByCourseIdsMetaData: {},

        courseTemplates: [],
        activeCourses: [],
        activeCoursesMetaData: {},
        currentCourseProvisions: [],
        currentCourseProvisionsMetaData: {},
        currentCourses: [],
        currentCoursesMetaData: {},
        currentCourse: {},
        currentModule: {},
        currentProvision: {},
        currentTemplate: {},

        currentUserId: '',
        loadingCourseAuditData: false,
        loadingCourse: false,
        loadingCourses: false,
        loadingActiveCourses: false,
        loadingCoursesByCourseIds: false,
        loadingUsersCoursesByCodeOrName: false,
        loadingCourseModule: false,
        schedulingCourseModule: false,
        loadingCourseModules: false,
        loadingModulesByParticipantAndDateRange: false,
        loadingCourseModulesByUsersAndDate: false,
        loadingCourseModulesByUserAndDateRange: false,
        loadingParticipantsCourses: false,
        loadingProvision: false,
        loadingProvisions: false,
        loadingCurrentProvisions: false,
        loadingSelfEmployedCourseModules: false,
        loadingTemplate: false,
        loadingTemplates: false,
        loadingUserId: '',
        participantsCourses: [],
        participantsCoursesMetaData: {},
        participantsModules: [],
        participantModulesAttendedCount: null,
        selfEmployedCourseModules: [],
        selfEmployedCourseModulesMetaData: {},
        usersCourses: [],
        usersCoursesMetaData: getEmptyPaginationState(),
        usersCoursesByCodeOrName: [],
        usersCoursesByCodeOrNameMetaData: getEmptyPaginationState(),
        usersCourseTemplates: [],

        openModuleScheduler: false,
        moduleDataKeys: {},
        participantDataKeys: {},
        successMessage: '',
        errorMessage: '',
        businessLogicError: {},
        version: {}
    },
    reducers: {
        // These update the state. They can use mutable code and Immer is used
        // to convert this to immutable code

        // AUDIT
        courseAuditDataLoading: (state) => {
            state.loadingCourseAuditData = true;
        },
        courseAuditDataLoaded: (state, action) => {
            state.loadingCourseAuditData = false;
            state.courseAuditData = action.payload;
        },
        courseAuditDataFailed: (state, action) => {
            state.loadingCourseAuditData = false;
            state.errorMessage = action.payload;
        },

        courseCreated: (state, action) => {
            state.loadingCourse = false;
            state.currentCourse = action.payload;
            state.courses = [...state.courses, action.payload];
            state.usersCourses = [...state.usersCourses, action.payload];
            state.coursesByCourseIds = [...state.coursesByCourseIds, action.payload];
        },

        courseLoaded: (state, action) => {
            state.loadingCourse = false;
            state.currentCourse = action.payload;
            state.courses = state.courses.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
            state.coursesByCourseIds = state.coursesByCourseIds.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
            state.usersCourses = state.usersCourses.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        courseLoading: (state) => {
            state.currentCourse = {};
            state.loadingCourse = true;
        },

        courseLoadingFailed: (state, action) => {
            state.loadingCourse = false;
            state.errorMessage = action.payload;
        },

        courseUpdated: (state, action) => {
            state.loadingCourse = false;
            state.currentCourse = action.payload;
            state.courses = state.courses.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
            state.usersCourses = state.usersCourses.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
            state.coursesByCourseIds = state.coursesByCourseIds.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
        },

        courseModuleLoading: (state) => {
            state.currentModule = {};
            state.loadingCourseModule = true;
        },

        courseModuleLoadingFailed: (state, action) => {
            state.loadingCourseModule = false;
            state.errorMessage = action.payload;
        },

        courseModulesByUsersAndDateLoading: (state) => {
            state.loadingCourseModulesByUsersAndDate = true;
        },

        courseModulesByUsersAndDateLoaded: (state, action) => {
            state.loadingCourseModulesByUsersAndDate = false;
            const { content, ...rest } = action.payload;
            if (content?.length > 0) {
                content?.forEach((el) => {
                    if (`${el.userId}_${el.date}` in state.courseModulesByUsersAndDate) {
                        state.courseModulesByUsersAndDate[`${el.userId}_${el.date}`] = [
                            ...state.courseModulesByUsersAndDate[`${el.userId}_${el.date}`],
                            el
                        ];
                    } else {
                        state.courseModulesByUsersAndDate = {
                            ...state.courseModulesByUsersAndDate,
                            [`${el.userId}_${el.date}`]: [el]
                        };
                    }
                });
            }
            state.courseModulesByUsersAndDateMetaData = rest;
        },

        courseModulesByUsersAndDateLoadingFailed: (state, action) => {
            state.loadingCourseModulesByUsersAndDate = false;
            state.errorMessage = action.payload;
        },

        modulesByParticipantAndDateRangeLoading: (state, action) => {
            state.loadingModulesByParticipantAndDateRange = true;
            state.participantDataKeys = action.payload;
        },
        modulesByParticipantAndDateRangeLoaded: (state, action) => {
            state.loadingModulesByParticipantAndDateRange = false;
            const { participantId, dateFrom, dateTo } = state.participantDataKeys;
            const { content, ...rest } = action.payload;
            if (
                `${participantId}_${dateFrom}_${dateTo}` in state.modulesByParticipantAndDateRange
            ) {
                state.modulesByParticipantAndDateRange[`${participantId}_${dateFrom}_${dateTo}`] =
                    rest.first
                        ? content
                        : [
                              ...state.modulesByParticipantAndDateRange[
                                  `${participantId}_${dateFrom}_${dateTo}`
                              ],
                              ...content
                          ];
            } else {
                state.modulesByParticipantAndDateRange = {
                    ...state.modulesByParticipantAndDateRange,
                    [`${participantId}_${dateFrom}_${dateTo}`]: content
                };
            }
            state.modulesByParticipantAndDateRangeMetaData = rest;
        },
        modulesByParticipantAndDateRangeLoadingFailed: (state, action) => {
            state.loadingModulesByParticipantAndDateRange = false;
            state.errorMessage = action.payload;
        },
        clearModulesByParticipantAndDateRange: (state) => {
            state.modulesByParticipantAndDateRange = {};
            state.modulesByParticipantAndDateRangeMetaData = {};
            state.participantDataKeys = {};
        },
        clearModulesByParticipantAndDateRangeMetaData: (state) => {
            state.modulesByParticipantAndDateRangeMetaData = {};
        },

        courseModulesByUserAndDateRangeLoading: (state, action) => {
            state.loadingCourseModulesByUserAndDateRange = true;
            state.moduleDataKeys = action.payload;
        },
        courseModulesByUserAndDateRangeLoaded: (state, action) => {
            state.loadingCourseModulesByUserAndDateRange = false;
            const { userId, dateFrom, dateTo } = state.moduleDataKeys;
            const { content, ...rest } = action.payload;
            if (`${userId}_${dateFrom}_${dateTo}` in state.courseModulesByUserAndDateRange) {
                state.courseModulesByUserAndDateRange[`${userId}_${dateFrom}_${dateTo}`] =
                    rest.first
                        ? content
                        : [
                              ...state.courseModulesByUserAndDateRange[
                                  `${userId}_${dateFrom}_${dateTo}`
                              ],
                              ...content
                          ];
            } else {
                state.courseModulesByUserAndDateRange = {
                    ...state.courseModulesByUserAndDateRange,
                    [`${userId}_${dateFrom}_${dateTo}`]: content
                };
            }
            state.courseModulesByUserAndDateRangeMetaData = rest;
            state.moduleDataKeys = {};
        },
        courseModulesByUserAndDateRangeLoadingFailed: (state, action) => {
            state.loadingCourseModulesByUserAndDateRange = false;
            state.errorMessage = action.payload;
        },
        clearCourseModulesByUserAndDateRange: (state) => {
            state.courseModulesByUserAndDateRange = {};
            state.courseModulesByUserAndDateRangeMetaData = {};
            state.moduleDataKeys = {};
        },

        courseModuleScheduling: (state, action) => {
            state.moduleDataKeys = action.payload;
        },
        courseModuleScheduled: (state, action) => {
            const { userId } = state.moduleDataKeys;
            state.newScheduledModules = {
                ...state.newScheduledModules,
                [userId]:
                    userId in state.newScheduledModules
                        ? [...state.newScheduledModules[userId], action.payload]
                        : [action.payload]
            };
            state.moduleDataKeys = {};
            state.currentModule = {};
        },
        clearNewScheduledModules: (state) => {
            state.newScheduledModules = {};
        },
        courseModuleRescheduled: (state, action) => {
            state.courseModulesByUserAndDateRange = Object.entries(
                state.courseModulesByUserAndDateRange
            ).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: val.map((el) => (el.id === action.payload.id ? action.payload : el))
                }),
                {}
            );
        },
        courseModuleSchedulingFailed: (state, action) => {
            state.errorMessage = action?.error;
        },

        courseModuleUpdated: (state, action) => {
            state.loadingCourseModule = false;

            const course = state.currentCourses.find((el) => el.id === action.payload.courseId);
            if (course) {
                const updatedCourse = {
                    ...course,
                    modules: course.modules.map((el) =>
                        el.id === action.payload.id ? action.payload : el
                    )
                };
                state.currentCourses = state.currentCourses.map((el) =>
                    el.id === updatedCourse.id ? updatedCourse : el
                );
            }

            state.usersCourses = state.usersCourses.map((el) =>
                el.id === action.payload.courseId
                    ? {
                          ...el,
                          modules: el.modules.map((entry) =>
                              entry.id === action.payload.id ? action.payload : entry
                          )
                      }
                    : el
            );
            // update modules and allModules in state
            const existingInModules = state.courseModules.some((el) => el.id === action.payload.id);

            state.courseModules = existingInModules
                ? state.courseModules.map((el) =>
                      el.id === action.payload.id ? action.payload : el
                  )
                : [...state.courseModules, action.payload];
            state.currentModule = action.payload;

            const userId = action.payload?.userId;
            if (!userId) return;
            state.courseModulesByUser[userId] =
                state.courseModulesByUser[userId] &&
                Array.isArray(state.courseModulesByUser[userId])
                    ? state.courseModulesByUser[userId].map((el) =>
                          el.id === action.payload.id ? action.payload : el
                      )
                    : [action.payload];
            if (
                `${action.payload.userId}_${action.payload.date}` in
                state.courseModulesByUsersAndDate
            ) {
                state.courseModulesByUsersAndDate[
                    `${action.payload.userId}_${action.payload.date}`
                ] = state.courseModulesByUsersAndDate[
                    `${action.payload.userId}_${action.payload.date}`
                ]?.map((el) => (el.id === action.payload.id ? action.payload : el));
            }
        },

        clearCourseAuditData: (state) => {
            state.courseAuditData = [];
        },

        clearSelfEmployedCourseModules: (state) => {
            state.selfEmployedCourseModules = [];
            state.selfEmployedCourseModulesMetaData = {};
        },

        selfEmployedCourseModulesLoaded: (state, action) => {
            state.loadingSelfEmployedCourseModules = false;
            const { content, ...rest } = action.payload;
            state.selfEmployedCourseModules = rest.first
                ? content
                : [...state.selfEmployedCourseModules, ...content];
            state.selfEmployedCourseModulesMetaData = rest;
        },

        selfEmployedCourseModulesLoading: (state) => {
            state.loadingSelfEmployedCourseModules = true;
        },

        selfEmployedCourseModulesUpdated: (state, action) => {
            state.loadingSelfEmployedCourseModules = false;
            state.selfEmployedCourseModules = state.selfEmployedCourseModules.map((el) => {
                const updated = action.payload.find((entry) => entry.id === el.id);
                return updated || el;
            });
        },

        selfEmployedCourseModulesLoadingFailed: (state, action) => {
            state.loadingSelfEmployedCourseModules = false;
            state.errorMessage = action.payload;
        },

        courseProvisionLoaded: (state, action) => {
            state.loadingProvision = false;
            state.currentProvision = action.payload;
        },

        courseProvisionLoading: (state) => {
            state.loadingProvision = true;
            state.courseProvision = {};
        },

        courseProvisionLoadingFailed: (state, action) => {
            state.loadingProvision = false;
            state.errorMessage = action.payload;
        },

        courseProvisionsLoaded: (state, action) => {
            state.loadingProvisions = false;
            const { content, ...rest } = action.payload;
            if (rest.first || state.courseProvisionsMetaData.number !== rest.number) {
                state.courseProvisions = rest.first
                    ? content
                    : [...state.courseProvisions, ...content];
                state.courseProvisionsMetaData = rest;
            }
        },

        courseProvisionsLoading: (state) => {
            state.loadingProvisions = true;
        },

        courseProvisionsLoadingFailed: (state, action) => {
            state.loadingProvisions = false;
            state.errorMessage = action.payload;
        },

        courseProvisionUpdated: (state, action) => {
            state.loadingProvision = false;
            const provisions = state.courseProvisions.filter((el) => el.id !== action.payload.id);
            state.courseProvisions = [...provisions, action.payload];
        },

        courseTemplateLoaded: (state, action) => {
            state.loadingTemplate = false;
            const templates = state.courseTemplates.filter((el) => el.id !== action.payload.id);
            state.courseTemplates = [...templates, action.payload];
            state.currentTemplate = action.payload;
        },

        courseTemplateLoading: (state) => {
            state.loadingTemplate = true;
            state.currentTemplate = {};
        },

        courseTemplateLoadingFailed: (state, action) => {
            state.loadingTemplate = false;
            state.errorMessage = action.payload;
        },

        courseTemplatesLoaded: (state, action) => {
            state.loadingTemplates = false;
            state.courseTemplates = action.payload;
        },

        courseTemplatesLoading: (state) => {
            state.loadingTemplates = true;
            state.courseTemplates = [];
        },

        courseTemplateUpdated: (state, action) => {
            state.loadingTemplate = false;
            const templates = state.courseTemplates.filter((el) => el.id !== action.payload.id);
            state.courseTemplates = [...templates, action.payload];
            state.usersCourseTemplates = state.usersCourseTemplates.map((el) =>
                el.id === action.payload.id ? action.payload : el
            );
            state.currentTemplate = action.payload;
        },

        currentCourseProvisionsLoaded: (state, action) => {
            state.loadingCurrentProvisions = false;
            const { content, ...rest } = action.payload;
            if (rest.first || state.currentCourseProvisionsMetaData.number !== rest.number) {
                state.currentCourseProvisions = rest.first
                    ? content
                    : [...state.currentCourseProvisions, ...content];
                state.currentCourseProvisionsMetaData = rest;
            }
        },

        currentCourseProvisionsLoading: (state) => {
            state.loadingCurrentProvisions = true;
        },

        currentCourseProvisionsLoadingFailed: (state, action) => {
            state.loadingCurrentProvisions = false;
            state.errorMessage = action.payload;
        },

        currentCoursesLoading: (state) => {
            state.loadingCurrentCourses = true;
        },

        currentCoursesLoaded: (state, action) => {
            state.loadingCurrentCourses = false;
            const { content, ...rest } = action.payload;
            if (rest.first || state.currentCoursesMetaData.number !== rest.number) {
                state.currentCourses = rest.first ? content : [...state.currentCourses, ...content];
                state.currentCoursesMetaData = rest;
            }
        },

        currentCoursesLoadingFailed: (state, action) => {
            state.loadingCurrentCourses = false;
            state.errorMessage = action.payload;
        },

        activeCoursesLoading: (state) => {
            state.loadingActiveCourses = true;
        },

        activeCoursesLoaded: (state, action) => {
            state.loadingActiveCourses = false;
            const { content, ...rest } = action.payload;
            if (rest.first || state.activeCoursesMetaData.number !== rest.number) {
                state.activeCourses = rest.first ? content : [...state.activeCourses, ...content];
                state.activeCoursesMetaData = rest;
            }
        },

        activeCoursesLoadingFailed: (state, action) => {
            state.loadingActiveCourses = false;
            state.errorMessage = action.payload;
        },

        participantsCoursesLoading: (state) => {
            state.loadingParticipantsCourses = true;
        },

        participantsCoursesLoaded: (state, action) => {
            state.loadingParticipantsCourses = false;
            const { content, ...rest } = action.payload;
            state.participantsCourses = rest.first
                ? content
                : [...state.participantsCourses, ...content];
            state.participantsCoursesMetaData = rest;
        },

        participantsCoursesLoadingFailed: (state, action) => {
            state.loadingParticipantsCourses = false;
            state.errorMessage = action.payload;
        },
        clearParticipantsCourses: (state) => {
            state.participantsCourses = [];
            state.participantsCoursesMetaData = {};
        },

        setCurrentCourse: (state, action) => {
            state.currentCourse = action.payload;
        },

        setCurrentModule: (state, action) => {
            state.currentModule = action.payload;
        },

        setCurrentProvision: (state, action) => {
            state.currentProvision = action.payload;
        },

        setCurrentTemplate: (state, action) => {
            state.currentTemplate = action.payload;
        },

        usersCourseTemplatesLoaded: (state, action) => {
            state.loadingTemplates = false;
            state.usersCourseTemplates = action.payload;
        },

        usersCourseTemplatesLoading: (state) => {
            state.loadingTemplates = true;
            state.usersCourseTemplates = [];
        },

        usersCourseTemplatesLoadingFailed: (state, action) => {
            state.loadingTemplates = false;
            state.errorMessage = action.payload;
        },

        usersCoursesByCodeOrNameLoading: (state) => {
            state.loadingUsersCoursesByCodeOrName = true;
        },

        usersCoursesByCodeOrNameLoaded: (state, action) => {
            state.loadingUsersCoursesByCodeOrName = false;
            const { content, ...rest } = action.payload;
            state.usersCoursesByCodeOrName = rest.first
                ? content
                : [...state.usersCoursesByCodeOrName, ...content];
            state.usersCoursesByCodeOrNameMetaData = rest;
        },

        usersCoursesByCodeOrNameLoadingFailed: (state, action) => {
            state.loadingUsersCoursesByCodeOrName = false;
            state.errorMessage = action.payload;
        },

        // used by My Courses
        usersCoursesLoading: (state) => {
            state.loadingCourses = true;
        },

        usersCoursesLoaded: (state, action) => {
            state.loadingCourses = false;
            const { content, ...rest } = action.payload;
            state.usersCourses = rest.first ? content : [...state.usersCourses, ...content];
            state.usersCoursesMetaData = rest;
        },

        usersCoursesLoadingFailed: (state, action) => {
            state.loadingCourses = false;
            state.errorMessage = action.payload;
        },

        setOpenModuleSchedulerPanel: (state, action) => {
            state.openModuleScheduler = action.payload;
        },

        errorMessageSet: (state, action) => {
            state.errorMessage = action.payload;
            state.successMessage = '';
        },

        errorOccurred: (state, action) => {
            state.errorMessage = action.payload;
        },

        businessLogicErrorOccurred: (state, action) => {
            state.businessLogicError = Object.entries(action.error).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: { error: true, message: value }
                }),
                {}
            );
        },

        clearBusinessLogicError: (state) => {
            state.businessLogicError = {};
        },

        successMessageSet: (state, action) => {
            state.successMessage = action.payload;
            state.errorMessage = '';
        },

        versionLoaded: (state, action) => {
            state.version = action.payload;
        }
    }
});

// ActionCreators. These are commands rather
// than events that have happened

// AUDIT
export const loadCourseAuditData = (type, id, field) => {
    const encodedField = encodeURIComponent(field);
    return apiCallBegan({
        url: getCourseURL() + `rest/${type}/audit/${id}/${encodedField}`,
        onStart: courseAuditDataLoading.type,
        onSuccess: courseAuditDataLoaded.type,
        successMessage: `Audit entry has been searched`,
        onError: courseAuditDataFailed.type
    });
};

// Courses
export const loadCourse = (id) =>
    apiCallBegan({
        url: getCourseURL() + `rest/course/${id}`,
        onSuccess: courseLoaded.type,
        onError: courseLoadingFailed.type
    });

export const loadCoursesByParticipantId = (
    participantId,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/course/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data: { participantIds: [participantId] },
        onStart: participantsCoursesLoading.type,
        onSuccess: participantsCoursesLoaded.type,
        onError: participantsCoursesLoadingFailed.type
    });

export const searchCoursesByCodeOrName = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'endDate',
    dir = 'desc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/course/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: usersCoursesByCodeOrNameLoading.type,
        onSuccess: usersCoursesByCodeOrNameLoaded.type,
        successMessage: `Course search record has been loaded`,
        onError: usersCoursesByCodeOrNameLoadingFailed.type
    });

export const searchCoursesByLoggedInUser = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'endDate',
    dir = 'desc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/course/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: usersCoursesLoading.type,
        onSuccess: usersCoursesLoaded.type,
        successMessage: `Course records by logged in user have been loaded`,
        onError: usersCoursesLoadingFailed.type
    });

export const searchActiveCourses = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/course/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: activeCoursesLoading.type,
        onSuccess: activeCoursesLoaded.type,
        onError: activeCoursesLoadingFailed.type
    });

export const searchCurrentCourses = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/course/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: currentCoursesLoading.type,
        onSuccess: currentCoursesLoaded.type,
        onError: currentCoursesLoadingFailed.type
    });

export const createCourse = (data) =>
    apiCallBegan({
        url: getCourseURL() + 'rest/course',
        method: 'post',
        data,
        onStart: courseLoading.type,
        onSuccess: courseCreated.type,
        successMessage: `Course details for ${data.name} have been added`,
        onError: courseLoadingFailed.type
    });

export const updateCourse = (data) =>
    apiCallBegan({
        url: getCourseURL() + `rest/course/${data.id}`,
        method: 'put',
        data,
        onStart: courseLoading.type,
        onSuccess: courseUpdated.type,
        successMessage: `Course details for ${data.name} have been updated`,
        onError: courseLoadingFailed.type
    });

// Modules
export const updateSelfEmployedCourseModules = (data) =>
    apiCallBegan({
        url: getCourseURL() + `rest/self-employed-course-module`,
        method: 'put',
        data,
        onStart: selfEmployedCourseModulesLoading.type,
        onSuccess: selfEmployedCourseModulesUpdated.type,
        successMessage: 'Self employed course modules have been updated',
        onError: selfEmployedCourseModulesLoadingFailed.type
    });

export const searchSelfEmployedCourseModules = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'desc'
) =>
    apiCallBegan({
        url:
            getCourseURL() +
            `rest/self-employed-course-module/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: selfEmployedCourseModulesLoading.type,
        onSuccess: selfEmployedCourseModulesLoaded.type,
        successMessage: `Modules have been loaded`,
        onError: selfEmployedCourseModulesLoadingFailed.type
    });

export const searchUsersModulesByDate = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'userId',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/module/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: courseModulesByUsersAndDateLoading.type,
        onSuccess: courseModulesByUsersAndDateLoaded.type,
        successMessage: `Modules have been loaded`,
        onError: courseModulesByUsersAndDateLoadingFailed.type
    });

// PT Calendar
export const searchModulesByParticipantAndDateRange = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'startDateTime',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/module/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStartData: data,
        onStart: modulesByParticipantAndDateRangeLoading.type,
        onSuccess: modulesByParticipantAndDateRangeLoaded.type,
        successMessage: `Modules have been loaded`,
        onError: modulesByParticipantAndDateRangeLoadingFailed.type
    });

export const searchCourseModulesByUserAndDateRange = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'userId',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() + `rest/module/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStartData: { ...data, userId: data.userIds[0] },
        onStart: courseModulesByUserAndDateRangeLoading.type,
        onSuccess: courseModulesByUserAndDateRangeLoaded.type,
        successMessage: `Modules have been loaded`,
        onError: courseModulesByUserAndDateRangeLoadingFailed.type
    });

export const scheduleCourseModule = (data) =>
    apiCallBegan({
        url: getCourseURL() + `rest/module/${data.id}`,
        method: 'put',
        data,
        onStartData: { userId: data.userId },
        onStart: courseModuleScheduling.type,
        onSuccess: courseModuleScheduled.type,
        successMessage: `Module ${data.title} has been scheduled`,
        onError: courseModuleSchedulingFailed.type
    });

export const rescheduleCourseModule = (data) =>
    apiCallBegan({
        url: getCourseURL() + `rest/module/${data.id}`,
        method: 'put',
        data,
        onSuccess: courseModuleRescheduled.type,
        successMessage: `Module ${data.title} has been rescheduled`,
        onError: courseModuleSchedulingFailed.type
    });

export const updateCourseModuleParticipants = (id, data, msg) =>
    apiCallBegan({
        url: getCourseURL() + `rest/module/${id}/participants`,
        method: 'put',
        data,
        onStart: courseModuleLoading.type,
        onSuccess: courseModuleUpdated.type,
        successMessage: msg,
        onError: courseModuleLoadingFailed.type,
        onBusinessLogicError: businessLogicErrorOccurred.type
    });

// Provisions
export const createCourseProvision = (title, data) =>
    apiCallBegan({
        url: getCourseURL() + 'rest/provision',
        method: 'post',
        data,
        onStart: courseProvisionLoading.type,
        onSuccess: courseProvisionUpdated.type, // new,
        successMessage: `Course Provision details for ${title} have been added`,
        onError: courseProvisionLoadingFailed.type
    });

export const loadCourseProvision = (id) =>
    apiCallBegan({
        url: getCourseURL() + `rest/provision/${id}`,
        onStart: courseProvisionLoading.type,
        onSuccess: courseProvisionLoaded.type,
        onError: courseProvisionLoadingFailed.type
    });

export const loadCourseProvisions = (
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url: getCourseURL() + `rest/provision?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        onStart: courseProvisionsLoading.type,
        onSuccess: courseProvisionsLoaded.type,
        onError: courseProvisionsLoadingFailed.type
    });

export const searchCourseProvisions = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() +
            `rest/provision/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: courseProvisionsLoading.type,
        onSuccess: courseProvisionsLoaded.type,
        onError: courseProvisionsLoadingFailed.type
    });

export const searchCurrentCourseProvisions = (
    data,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE,
    sort = 'name',
    dir = 'asc'
) =>
    apiCallBegan({
        url:
            getCourseURL() +
            `rest/provision/search?page=${page}&size=${size}&sort=${sort}&dir=${dir}`,
        method: 'post',
        data,
        onStart: currentCourseProvisionsLoading.type,
        onSuccess: currentCourseProvisionsLoaded.type,
        onError: currentCourseProvisionsLoadingFailed.type
    });

export const updateCourseProvision = (msg, data) =>
    apiCallBegan({
        url: getCourseURL() + `rest/provision/${data.id}`,
        method: 'put',
        data,
        onStart: courseProvisionLoading.type,
        onSuccess: courseProvisionUpdated.type,
        successMessage: msg,
        onError: courseProvisionLoadingFailed.type
    });

export const updateCourseProvisionParticipants = (id, data, msg) =>
    apiCallBegan({
        url: getCourseURL() + `rest/provision/${id}/participants`,
        method: 'put',
        data,
        onStart: courseProvisionLoading.type,
        onSuccess: courseProvisionUpdated.type,
        successMessage: msg,
        onError: courseProvisionLoadingFailed.type
    });

// Templates
export const createCourseTemplate = (name, data) =>
    apiCallBegan({
        url: getCourseURL() + 'rest/course-template',
        method: 'post',
        data,
        onStart: courseTemplateLoading.type,
        onSuccess: courseTemplateLoaded.type, // new
        successMessage: `Course template details for ${name} have been added`,
        onError: courseTemplateLoadingFailed.type
    });

export const loadCourseTemplate = (id) =>
    apiCallBegan({
        url: getCourseURL() + `rest/course-template/${id}`,
        onStart: courseTemplateLoading.type,
        onSuccess: courseTemplateLoaded.type,
        onError: courseTemplateLoadingFailed.type
    });

export const loadCourseTemplatesByUserId = (userId) =>
    apiCallBegan({
        url: getCourseURL() + 'rest/course-template/search',
        method: 'post',
        data: { userIds: [userId] },
        onStart: usersCourseTemplatesLoading.type,
        onSuccess: usersCourseTemplatesLoaded.type,
        onError: usersCourseTemplatesLoadingFailed.type
    });

export const loadCourseTemplatesByContractIds = (contractIds) =>
    apiCallBegan({
        url: getCourseURL() + 'rest/course-template/search',
        method: 'post',
        data: { contractIds },
        onStart: courseTemplatesLoading.type,
        onSuccess: courseTemplatesLoaded.type,
        onError: usersCourseTemplatesLoadingFailed.type
    });

export const updateCourseTemplate = (data) =>
    apiCallBegan({
        url: getCourseURL() + `rest/course-template/${data.id}`,
        method: 'put',
        data,
        onStart: courseTemplateLoading.type,
        onSuccess: courseTemplateUpdated.type,
        successMessage: `Course template details for ${data.name} have been updated`,
        onError: courseTemplateLoadingFailed.type
    });

export const loadCourseServiceVersion = () =>
    apiVersion({
        url: getCourseURL() + 'rest/version',
        method: 'get',
        onSuccess: versionLoaded.type,
        onError: errorOccurred.type
    });

export const setCurrentlySelectedCourseTemplate = (courseTemplate) =>
    courseTemplateUpdated(courseTemplate);

export const setCurrentlySelectedCourse = (course) => courseUpdated(course);

// For external use
export const {
    clearBusinessLogicError,
    clearCourseAuditData,
    clearCourseModulesByUserAndDateRange,
    clearModulesByParticipantAndDateRange,
    clearModulesByParticipantAndDateRangeMetaData,
    clearNewScheduledModules,
    clearParticipantsCourses,
    clearSelfEmployedCourseModules,

    setCurrentCourse,
    setCurrentModule,
    setCurrentProvision,
    setCurrentTemplate,
    setOpenModuleSchedulerPanel
} = slice.actions;

// For internal use
const {
    activeCoursesLoading,
    activeCoursesLoadingFailed,
    activeCoursesLoaded,
    courseAuditDataLoading,
    courseAuditDataLoaded,
    courseAuditDataFailed,
    courseCreated,
    courseLoaded,
    courseLoading,
    courseLoadingFailed,
    courseModuleUpdated,
    courseModuleRescheduled,
    courseModuleScheduling,
    courseModuleScheduled,
    courseModuleSchedulingFailed,
    courseModuleLoading,
    courseModuleLoadingFailed,
    modulesByParticipantAndDateRangeLoading,
    modulesByParticipantAndDateRangeLoaded,
    modulesByParticipantAndDateRangeLoadingFailed,
    courseModulesByUsersAndDateLoading,
    courseModulesByUsersAndDateLoaded,
    courseModulesByUsersAndDateLoadingFailed,
    courseModulesByUserAndDateRangeLoading,
    courseModulesByUserAndDateRangeLoaded,
    courseModulesByUserAndDateRangeLoadingFailed,
    courseUpdated,
    courseProvisionLoaded,
    courseProvisionLoading,
    courseProvisionLoadingFailed,
    courseProvisionsLoaded,
    courseProvisionsLoading,
    courseProvisionsLoadingFailed,
    courseProvisionUpdated,
    courseTemplateLoaded,
    courseTemplateLoading,
    courseTemplateLoadingFailed,
    courseTemplatesLoaded,
    courseTemplatesLoading,
    courseTemplateUpdated,
    currentCoursesLoading,
    currentCoursesLoadingFailed,
    currentCoursesLoaded,
    currentCourseProvisionsLoading,
    currentCourseProvisionsLoaded,
    currentCourseProvisionsLoadingFailed,
    participantsCoursesLoading,
    participantsCoursesLoaded,
    participantsCoursesLoadingFailed,
    selfEmployedCourseModulesLoaded,
    selfEmployedCourseModulesLoading,
    selfEmployedCourseModulesUpdated,
    selfEmployedCourseModulesLoadingFailed,
    usersCoursesByCodeOrNameLoaded,
    usersCoursesByCodeOrNameLoading,
    usersCoursesByCodeOrNameLoadingFailed,
    usersCoursesLoading,
    usersCoursesLoaded,
    usersCoursesLoadingFailed,
    usersCourseTemplatesLoading,
    usersCourseTemplatesLoaded,
    usersCourseTemplatesLoadingFailed,

    errorOccurred,
    businessLogicErrorOccurred,
    versionLoaded
} = slice.actions;

export default slice.reducer;
